import { useQuery } from '@tanstack/react-query';
import type { Limits, Role } from '@lixionary-jira/domain';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { PageLoading } from '../ui/feedback';
import { ApiError, api, setCsrfToken } from './client';

export interface Me {
  user: { id: string; displayName: string; email: string | null; role: Role };
  csrfToken: string;
  sessionExpiresAt: string;
  idleMinutes: number;
  unreadNotifications: number;
  limits: Limits;
  devAuth?: boolean;
}

const MeContext = createContext<Me | null>(null);

export function useMe(): Me {
  const me = useContext(MeContext);
  if (!me) throw new Error('useMe outside RequireSession');
  return me;
}
export const useIsAdmin = () => useMe().user.role === 'admin';

export const meQueryKey = ['me'] as const;

/** Gate for authenticated pages; restores the requested destination after sign-in. */
export function RequireSession({ children }: { children: ReactNode }) {
  const location = useLocation();
  const q = useQuery({
    queryKey: meQueryKey,
    queryFn: () => api.get<Me>('/me'),
    retry: false,
    refetchInterval: 60_000,
  });
  useEffect(() => {
    if (q.data) setCsrfToken(q.data.csrfToken);
  }, [q.data]);
  if (q.isPending) return <PageLoading />;
  if (q.error) {
    const returnTo = `${location.pathname}${location.search}`;
    const expired =
      q.error instanceof ApiError &&
      q.error.status === 401 &&
      sessionStorage.getItem('lj_had_session') === '1';
    return (
      <Navigate
        to={`/sign-in?returnTo=${encodeURIComponent(returnTo)}${expired ? '&expired=1' : ''}`}
        replace
      />
    );
  }
  sessionStorage.setItem('lj_had_session', '1');
  setCsrfToken(q.data.csrfToken);
  return <MeContext.Provider value={q.data}>{children}</MeContext.Provider>;
}
